/**
 * Patient Packages — pure logic (PRD.261).
 *
 * Dependency-free helpers for payment/redemption status derivation and
 * validity math. Import-free on purpose so they can be unit-tested with Node's
 * built-in runner and reused by the web-admin patient-package service.
 */

export type PackagePaymentStatus = 'unpaid' | 'partially_paid' | 'paid' | 'voided';
export type PackageRedemptionStatus = 'pending_payment' | 'active' | 'suspended' | 'voided';

/**
 * Derive package payment status from the source invoice's paid vs grand totals
 * (in minor units / cents). Mirrors the invoice's own settlement logic:
 * nothing paid → unpaid, fully covered → paid, otherwise partially_paid.
 */
export function derivePaymentStatus(paidCents: number, grandCents: number): PackagePaymentStatus {
  if (paidCents <= 0) return 'unpaid';
  if (grandCents > 0 && paidCents >= grandCents) return 'paid';
  return 'partially_paid';
}

/**
 * Next redemption status after a payment sync (PRD.261 §10). A package that is
 * still pending payment becomes active on the first successful payment
 * (partially_paid or paid). Activation is sticky — once active it is never
 * auto-reverted here. Voided/suspended states are left untouched.
 */
export function nextRedemptionStatus(
  current: PackageRedemptionStatus,
  paymentStatus: PackagePaymentStatus,
): PackageRedemptionStatus {
  if (
    current === 'pending_payment' &&
    (paymentStatus === 'partially_paid' || paymentStatus === 'paid')
  ) {
    return 'active';
  }
  return current;
}

/** A package cannot be activated or suspended once voided or deleted. */
export function canTransition(input: {
  redemptionStatus: PackageRedemptionStatus;
  deletedAt: string | null;
}): boolean {
  return input.redemptionStatus !== 'voided' && !input.deletedAt;
}

/**
 * Compute a package's expiry date (PRD.261 §4): purchase_date +
 * validity_months. Returns null when validity is null/invalid (no expiry).
 * `purchaseDate` is a `YYYY-MM-DD` string; the day is clamped to the last day
 * of the target month (e.g. Jan 31 + 1 month → Feb 28/29).
 */
export function computeValidUntil(
  purchaseDate: string,
  validityMonths: number | null | undefined,
): string | null {
  if (validityMonths === null || validityMonths === undefined) return null;
  if (!Number.isInteger(validityMonths) || validityMonths <= 0) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(purchaseDate);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]); // 1-12
  const day = Number(match[3]);

  const zeroBased = month - 1 + validityMonths;
  const targetYear = year + Math.floor(zeroBased / 12);
  const targetMonth = zeroBased % 12; // 0-11
  const lastDay = daysInMonth(targetYear, targetMonth);
  const targetDay = Math.min(day, lastDay);
  return `${pad4(targetYear)}-${pad2(targetMonth + 1)}-${pad2(targetDay)}`;
}

/** Initial balance for a snapshotted package item: nothing redeemed yet. */
export function initialItemBalance(totalQuantity: number): {
  totalQuantity: number;
  redeemedQuantity: number;
  remainingQuantity: number;
  status: 'available';
} {
  return {
    totalQuantity,
    redeemedQuantity: 0,
    remainingQuantity: totalQuantity,
    status: 'available',
  };
}

// ─── Patient package creation planner (pure) ───

/** A package invoice line to (maybe) turn into patient package ownership. */
export interface PackageLineInput {
  invoiceItemId: string;
  packageId: string;
  name: string | null;
  code: string | null;
  price: number | null;
  validityMonths: number | null;
}

/** An active composition child of a package, used to snapshot item balances. */
export interface PackageChildInput {
  childInventoryItemId: string;
  quantity: number | null;
  includedUnitPrice: number | null;
  sortOrder: number | null;
  name: string | null;
  code: string | null;
  itemType: string | null;
  sellingPrice: number | null;
}

export interface PlannedPatientPackage {
  id: string;
  source_invoice_item_id: string;
  package_inventory_item_id: string;
  package_name_snapshot: string;
  package_code_snapshot: string | null;
  package_price_snapshot: number;
  purchase_date: string;
  valid_until: string | null;
  validity_months_snapshot: number | null;
}

export interface PlannedPatientPackageItem {
  id: string;
  patient_package_id: string;
  child_inventory_item_id: string;
  item_name_snapshot: string;
  item_code_snapshot: string | null;
  item_type_snapshot: string;
  total_quantity: number;
  redeemed_quantity: number;
  remaining_quantity: number;
  unit_price_snapshot: number | null;
  sort_order: number;
}

export interface PatientPackageCreationPlan {
  packages: PlannedPatientPackage[];
  items: PlannedPatientPackageItem[];
  skipped: { packageName: string; reason: string }[];
}

/**
 * Plan patient package creation from an invoice's package lines (PRD.261
 * §2, §5, §6, §13). Pure — no DB access — so the idempotency, snapshot, and
 * skip rules are unit-testable. The imperative service fetches rows, calls
 * this, then executes the returned inserts.
 *
 * - Lines whose `invoiceItemId` is already owned (in `existingSourceItemIds`)
 *   are skipped silently — this is what makes creation idempotent across saves
 *   and offline replays.
 * - Lines whose package has no active included items are skipped with a
 *   user-facing reason (an empty package is not sellable/redeemable).
 * - Item balances are snapshotted with remaining = total, redeemed = 0.
 *
 * `newId` produces unique ids (crypto.randomUUID in the app; a counter in
 * tests) so the planner stays deterministic and side-effect free.
 */
export function planPatientPackageCreation(input: {
  purchaseDate: string;
  lines: readonly PackageLineInput[];
  existingSourceItemIds: readonly string[];
  compositionByPackage: Record<string, readonly PackageChildInput[]>;
  newId: () => string;
}): PatientPackageCreationPlan {
  const { purchaseDate, lines, existingSourceItemIds, compositionByPackage, newId } = input;
  const existing = new Set(existingSourceItemIds);
  const plan: PatientPackageCreationPlan = { packages: [], items: [], skipped: [] };

  for (const line of lines) {
    if (existing.has(line.invoiceItemId)) continue; // idempotent

    const composition = compositionByPackage[line.packageId] ?? [];
    if (composition.length === 0) {
      plan.skipped.push({
        packageName: line.name ?? 'Package',
        reason: 'This package has no included items. Please update the package before selling it.',
      });
      continue;
    }

    const patientPackageId = newId();
    plan.packages.push({
      id: patientPackageId,
      source_invoice_item_id: line.invoiceItemId,
      package_inventory_item_id: line.packageId,
      package_name_snapshot: line.name ?? 'Package',
      package_code_snapshot: line.code ?? null,
      package_price_snapshot: line.price ?? 0,
      purchase_date: purchaseDate,
      valid_until: computeValidUntil(purchaseDate, line.validityMonths),
      validity_months_snapshot: line.validityMonths ?? null,
    });

    const ordered = [...composition].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    ordered.forEach((child, i) => {
      const total = child.quantity ?? 0;
      const balance = initialItemBalance(total);
      plan.items.push({
        id: newId(),
        patient_package_id: patientPackageId,
        child_inventory_item_id: child.childInventoryItemId,
        item_name_snapshot: child.name ?? 'Item',
        item_code_snapshot: child.code ?? null,
        item_type_snapshot: child.itemType ?? 'product',
        total_quantity: balance.totalQuantity,
        redeemed_quantity: balance.redeemedQuantity,
        remaining_quantity: balance.remainingQuantity,
        unit_price_snapshot: child.includedUnitPrice ?? child.sellingPrice ?? null,
        sort_order: child.sortOrder ?? i,
      });
    });
  }

  return plan;
}

function daysInMonth(year: number, monthZeroBased: number): number {
  // monthZeroBased: 0=Jan … 11=Dec
  return [31, isLeap(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][monthZeroBased];
}

function isLeap(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function pad4(n: number): string {
  return String(n).padStart(4, '0');
}
